import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { map, switchMap, take } from 'rxjs/operators';
import { combineLatest, Observable, Subscription } from 'rxjs';

import {
  FinnairAdvancePassengerInformationRequestV2,
  FinnairApisRequirementCountryOfResidence,
  FinnairApisRequirementDestinationAddress,
  FinnairApisRequirementHomeAddress,
  FinnairApisRequirementKeyEnum,
} from '@fcom/dapi/api/models';
import { ADDRESS_PATTERN, CITY_PATTERN, STATE_PATTERN, unsubscribe, ZIP_CODE_PATTERN } from '@fcom/core/utils';
import { finShare } from '@fcom/rx/operators/finShare';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { SelectOption } from '@fcom/common/interfaces';
import { ButtonType } from '@fcom/ui-components/components/buttons';
import { NotificationTheme } from '@fcom/ui-components/components/notifications';

import { FinnairPassengerItemWithApis } from '../../../check-in/interface/check-in.interface';
import { ManageBookingService } from '../../../../services';

export type AddressType =
  | FinnairApisRequirementKeyEnum.DESTINATION_ADDRESS
  | FinnairApisRequirementKeyEnum.HOME_ADDRESS;

@Component({
  selector: 'fin-apis-destination',
  templateUrl: 'apis-destination.component.html',
  styleUrls: ['apis-destination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApisDestinationComponent implements OnInit, OnDestroy {
  readonly ButtonType = ButtonType;
  readonly FinnairApisRequirementKeyEnum = FinnairApisRequirementKeyEnum;
  readonly NotificationTheme = NotificationTheme;

  @Input()
  passenger$: Observable<FinnairPassengerItemWithApis>;

  @Input()
  boundId$: Observable<string>;

  @Input()
  addressType: AddressType;

  @Input()
  arrivalCountryCode$: Observable<string>;

  @Output() completed = new EventEmitter<void>();

  form: FormGroup<{
    address: FormControl<string>;
    city: FormControl<string>;
    countryCode: FormControl<string>;
    state: FormControl<string>;
    zipCode: FormControl<string>;
  }>;

  countryCodes$: Observable<SelectOption[]>;
  countryRegions$: Observable<SelectOption[]>;
  countryName$: Observable<string>;
  loading = false;

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private manageBookingService: ManageBookingService
  ) {}

  ngOnInit(): void {
    const countries$ = this.languageService.translate('countryCodes').pipe(
      map((codes) => {
        if (!Array.isArray(codes)) {
          return [];
        }

        return codes;
      }),
      finShare()
    );

    this.countryCodes$ = countries$.pipe(
      map((codes) => codes.map((code) => ({ value: code.countryCode, name: code.name }))),
      finShare()
    );

    this.countryName$ = combineLatest([this.arrivalCountryCode$, this.countryCodes$, this.passenger$]).pipe(
      map(([arrivalCountryCode, countryCodes, passenger]) => {
        const countryOfResidence: FinnairApisRequirementCountryOfResidence = passenger.apisCpr?.requirements.find(
          (r) => r.key === FinnairApisRequirementKeyEnum.COUNTRY_OF_RESIDENCE
        )?.data as FinnairApisRequirementCountryOfResidence;
        const countryCode =
          this.addressType === FinnairApisRequirementKeyEnum.HOME_ADDRESS
            ? (countryOfResidence?.countryOfResidence ?? arrivalCountryCode)
            : arrivalCountryCode;

        const country = countryCodes.find((code) => code.value === countryCode);
        return country ? country.name : arrivalCountryCode;
      })
    );

    this.countryRegions$ = combineLatest([this.arrivalCountryCode$, this.passenger$]).pipe(
      map(([arrivalCountryCode, passenger]) => this.getCountryCode(this.addressType, passenger, arrivalCountryCode)),
      switchMap((countryCode) =>
        this.languageService.translate(`COUNTRY_REGIONS.${countryCode?.toLowerCase()}`, {}, { logErrors: false })
      ),
      map((regions) => {
        if (!regions) {
          return [];
        }

        return Object.keys(regions).map((region) => ({ value: region.toUpperCase(), name: regions[region] }));
      })
    );

    this.subscriptions.add(
      combineLatest([this.passenger$, this.arrivalCountryCode$])
        .pipe(take(1))
        .subscribe(([passenger, arrivalCountryCode]) => {
          const address = passenger.apisCpr?.requirements?.find((requirement) => requirement.key === this.addressType)
            ?.data as FinnairApisRequirementDestinationAddress | FinnairApisRequirementHomeAddress;

          const addressValue =
            (this.addressType === FinnairApisRequirementKeyEnum.DESTINATION_ADDRESS
              ? (address as FinnairApisRequirementDestinationAddress)?.destinationAddress
              : (address as FinnairApisRequirementHomeAddress)?.homeAddress) ?? '';

          this.form = new FormGroup({
            address: new FormControl(addressValue, [
              Validators.required,
              Validators.minLength(1),
              Validators.maxLength(35),
              Validators.pattern(ADDRESS_PATTERN),
            ]),
            city: new FormControl(address?.city ?? '', [
              Validators.required,
              Validators.maxLength(35),
              Validators.pattern(CITY_PATTERN),
            ]),
            countryCode: new FormControl(
              this.getCountryCode(this.addressType, passenger, arrivalCountryCode),
              Validators.required
            ),
            state: new FormControl(address?.state ?? '', [Validators.required, Validators.pattern(STATE_PATTERN)]),
            zipCode: new FormControl(address?.zipCode ?? '', [
              Validators.required,
              Validators.minLength(1),
              Validators.maxLength(20),
              Validators.pattern(ZIP_CODE_PATTERN),
            ]),
          });
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  saveAndContinue(): void {
    if (this.form.valid) {
      this.form.disable();
      this.loading = true;

      if (this.form.dirty) {
        const request$ = combineLatest([this.boundId$, this.passenger$]).pipe(
          map(([boundId, passenger]) => [
            {
              advancePassengerInformation: [
                {
                  [this.addressType === FinnairApisRequirementKeyEnum.DESTINATION_ADDRESS
                    ? 'destinationAddress'
                    : 'homeAddress']: this.form.value,
                  travelerId: passenger.id,
                },
              ],
              boundId,
            } as FinnairAdvancePassengerInformationRequestV2,
          ])
        );
        this.subscriptions.add(
          request$
            .pipe(
              take(1),
              switchMap((request) => this.manageBookingService.updateRegulatoryDetails(request))
            )
            .subscribe({
              next: () => {
                this.form.enable();
                this.loading = false;
                this.completed.emit();
              },
              error: () => {
                this.form.enable();
                this.loading = false;
              },
            })
        );
      } else {
        this.completed.emit();
      }
    } else {
      Object.keys(this.form.controls).forEach((field) => {
        const control = this.form.get(field);
        control.markAsTouched({ onlySelf: true });
      });
    }
  }

  private getCountryCode(
    addressType: AddressType,
    passenger: FinnairPassengerItemWithApis,
    arrivalCountryCode = ''
  ): string {
    const address = passenger.apisCpr?.requirements?.find((requirement) => requirement.key === this.addressType)
      ?.data as FinnairApisRequirementDestinationAddress | FinnairApisRequirementHomeAddress;

    const countryCode =
      addressType === FinnairApisRequirementKeyEnum.DESTINATION_ADDRESS
        ? (address as FinnairApisRequirementDestinationAddress)?.destinationCountry
        : (address as FinnairApisRequirementHomeAddress)?.homeCountry;

    if (countryCode) {
      return countryCode;
    }

    const countryOfResidence: FinnairApisRequirementCountryOfResidence = passenger.apisCpr?.requirements.find(
      (r) => r.key === FinnairApisRequirementKeyEnum.COUNTRY_OF_RESIDENCE
    )?.data as FinnairApisRequirementCountryOfResidence;

    return addressType === FinnairApisRequirementKeyEnum.HOME_ADDRESS
      ? (countryOfResidence?.countryOfResidence ?? arrivalCountryCode)
      : arrivalCountryCode;
  }
}
